import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ColumnDef,
    functionalUpdate,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Badge, Button } from "@telia/teddy";
import PAGES from "../../../../../constants/pages";
import { SearchResultDTO } from "../../../../../models/external/ocmInterface";
import * as properNameCase from "../../../../../shared/properNameCase";
import { API_URL_DELETE_DRAFT_ORDER, API_URL_GET_DRAFT_ORDERS } from "../../api";
import { retailerFetch } from "../../retailerFetch";

export interface DraftOrderItem {
    legalOwnerName?: string;
    orderLastModified?: number;
    order_created?: number;
    referenceId: string;
    salesAgentId?: string;
    status?: string;
}

type BadgeVariant = "attention" | "error" | "information" | "neutral" | "special" | "success" | "warning";

const statusVariant = (status: string | undefined): BadgeVariant => {
    switch (status?.toUpperCase()) {
        case "DRAFT":
            return "information";
        default:
            return "neutral";
    }
};

const getStatusText = (status: string | undefined) => {
    switch (status?.toUpperCase()) {
        case "DRAFT":
            return "Utkast";
        default:
            return status || "Ukjent status";
    }
};

const fetchDraftOrders = async (agentId = ""): Promise<DraftOrderItem[]> => {
    const url = new URL(API_URL_GET_DRAFT_ORDERS(), window.location.href);
    if (agentId) url.searchParams.set("agentId", agentId);
    const response = await retailerFetch(url.toString());
    if (!response.ok) throw new Error("Kunne ikke hente ordre. Prøv igjen senere.");
    const result: SearchResultDTO = await response.json();
    const hits = result?.hits || {};
    return Object.entries(hits).map(([referenceId, hit]) => ({
        legalOwnerName:
            [hit.order_content?.persons?.legalOwner?.first_name, hit.order_content?.persons?.legalOwner?.surname]
                .filter(Boolean)
                .join(" ") || undefined,
        orderLastModified: hit.order_last_modified,
        referenceId,
        salesAgentId:
            hit.order_content?.orderer?.sales_agent?.sales_agent_id || hit.order_content?.sales_agent?.sales_agent_code,
        status: hit.status,
    }));
};

const deleteDraftOrderRequest = async (referenceId: string): Promise<void> => {
    const response = await retailerFetch(API_URL_DELETE_DRAFT_ORDER(referenceId), { method: "DELETE" });
    if (!response.ok) throw new Error("Kunne ikke slette ordreutkastet. Prøv igjen senere.");
};

const EMPTY_DRAFT_ORDERS: DraftOrderItem[] = [];
const PAGE_SIZE = 25;

interface UseDraftOrdersTableOptions {
    agentId: string;
    enabled: boolean;
    onPageIndexChange: (pageIndex: number) => void;
    pageIndex: number;
    search: string;
}

const useDraftOrdersTable = ({
    agentId,
    enabled,
    onPageIndexChange,
    pageIndex,
    search,
}: UseDraftOrdersTableOptions) => {
    const queryClient = useQueryClient();
    const { data, isLoading, error } = useQuery({
        queryKey: ["draftOrders", agentId],
        queryFn: () => fetchDraftOrders(agentId),
        enabled,
    });
    const orders = data ?? EMPTY_DRAFT_ORDERS;
    const pagination = { pageIndex, pageSize: PAGE_SIZE };
    const [continueOrderRef, setContinueOrderRef] = useState<string | null>(null);
    const [deleteOrderRef, setDeleteOrderRef] = useState<string | null>(null);

    const deleteMutation = useMutation({
        mutationFn: (referenceId: string) => deleteDraftOrderRequest(referenceId),
        onSuccess: () => {
            setDeleteOrderRef(null);
            queryClient.invalidateQueries({ queryKey: ["draftOrders", agentId] });
        },
    });

    const filteredData = useMemo(() => {
        let result = orders as DraftOrderItem[];

        if (search) {
            const searchLower = search.toLowerCase();
            result = result.filter(
                (order) =>
                    order.referenceId.toLowerCase().includes(searchLower) ||
                    (order.legalOwnerName?.toLowerCase().includes(searchLower) ?? false) ||
                    (order.status?.toLowerCase().includes(searchLower) ?? false),
            );
        }

        return result.slice().sort((a, b) => (b.orderLastModified ?? 0) - (a.orderLastModified ?? 0));
    }, [orders, search]);

    useEffect(() => {
        if (!enabled || !data) return;
        const lastPageIndex = Math.max(0, Math.ceil(filteredData.length / PAGE_SIZE) - 1);
        if (pageIndex > lastPageIndex) onPageIndexChange(lastPageIndex);
    }, [data, enabled, filteredData.length, onPageIndexChange, pageIndex]);

    const handleContinueOrder = (orderRef: string) => {
        setContinueOrderRef(orderRef);
    };

    const handleDeleteOrder = (orderRef: string) => {
        setDeleteOrderRef(orderRef);
    };

    const handleViewDetails = (orderRef: string) => {
        window.location.assign(`${PAGES.RETAILER.ORDERS_OVERVIEW.DRAFT_DETAIL.URL}${encodeURIComponent(orderRef)}/`);
    };

    const columns = useMemo<ColumnDef<DraftOrderItem>[]>(
        () => [
            {
                accessorKey: "referenceId",
                id: "ref",
                header: "Ordrereferanse",
                cell: ({ getValue }) => (
                    <Badge variant="neutral" hideIcon>
                        {getValue<string>().slice(0, 10)}
                    </Badge>
                ),
            },
            {
                accessorKey: "legalOwnerName",
                id: "legalOwnerName",
                header: "Kunde",
                cell: ({ getValue }) => <span data-di-mask>{properNameCase.convert(getValue<string>() ?? "")}</span>,
            },
            {
                accessorKey: "status",
                id: "status",
                header: "Status",
                cell: ({ getValue }) => {
                    const val = getValue<string | undefined>();
                    if (!val) return "";
                    return (
                        <Badge variant={statusVariant(val)} hideIcon>
                            {getStatusText(val)}
                        </Badge>
                    );
                },
            },
            {
                accessorKey: "salesAgentId",
                id: "salesAgentId",
                header: "Selger",
                cell: ({ getValue }) => <span data-di-mask>{getValue<string | undefined>() ?? ""}</span>,
            },
            {
                accessorKey: "orderLastModified",
                id: "date",
                header: "Ordre sist endret",
                cell: ({ getValue }) => {
                    const val = getValue<number | undefined>();
                    return val
                        ? new Date(val).toLocaleString("nb-NO", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                          })
                        : "";
                },
            },
            {
                accessorKey: "actions",
                id: "actions",
                header: "",
                cell: ({ row }) => (
                    <div className="orders__td-actions">
                        <Button
                            variant="text"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(row.original.referenceId);
                            }}
                        >
                            Se detaljer
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleContinueOrder(row.original.referenceId);
                            }}
                        >
                            Fortsett ordren
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteOrder(row.original.referenceId);
                            }}
                        >
                            Slett
                        </Button>
                    </div>
                ),
            },
        ],
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
        [],
    );

    const table = useReactTable({
        columns,
        data: filteredData,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        autoResetPageIndex: false,
        state: { pagination },
        onPaginationChange: (updater) => onPageIndexChange(functionalUpdate(updater, pagination).pageIndex),
    });

    const onRowClick = (row: DraftOrderItem) => handleViewDetails(row.referenceId);

    return {
        columns,
        continueOrderRef,
        deleteOrderRef,
        deleteMutation,
        error,
        isLoading,
        onRowClick,
        setContinueOrderRef,
        setDeleteOrderRef,
        table,
    };
};

export default useDraftOrdersTable;
